import { defineComponent as _defineComponent } from 'vue'
import mapboxgl from 'mapbox-gl'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useJobFilterStore } from '../JobsFilterStore'
import GeoJsonSource from '../mapbox/GeoJsonSource.vue'
import LayerGroup from '../mapbox/LayerGroup.vue'
import { convertToSnakeCase } from '@/models/utils/casing'
import {
  RequestInterceptor,
  useMapboxRequestInterceptor,
} from '../mapbox/RequestInterceptor'
import { useFilteredJobStoreLegacyAdapter } from '@/components/jobs/job-search-page/filteredJobStore'


export default /*#__PURE__*/_defineComponent({
  __name: 'JobsLayer',
  props: {
    layerId: null,
    sourceId: null,
    before: null,
    hideLayer: { type: Boolean },
    hiddenLocationIds: null
  },
  emits: ["click"],
  setup(__props: any, { emit: emits }: { emit: ({
  (
    event: 'click',
    data: mapboxgl.MapboxGeoJSONFeature[],
    eventData: mapboxgl.MapMouseEvent & mapboxgl.EventData
  )
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  layerId: string
  sourceId: string
  before?: string
  hideLayer?: boolean
  hiddenLocationIds?: (string | number)[]
}





const source = ref()
const refreshLayer = () => {
  if (source.value && typeof source.value.refreshData === 'function') {
    source.value.refreshData()
  }
}
const pollingInterval = ref()
const jobsPolling = () => {
  refreshLayer()
  pollingInterval.value = setTimeout(jobsPolling, 30000)
}

const handleVisibilityChange = () => {
  if (document.hidden) {
    clearTimeout(pollingInterval.value)
  } else {
    jobsPolling()
  }
}

onMounted(() => {
  jobsPolling()
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  clearTimeout(pollingInterval.value)
  pollingInterval.value = null
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})

const jobsFilterStore = useJobFilterStore()
const legacyJobStore = useFilteredJobStoreLegacyAdapter()

jobsFilterStore.$subscribe(() => {
  source.value?.refreshData()
})

const handleClick = (
  data: mapboxgl.MapboxGeoJSONFeature[],
  eventData: mapboxgl.MapMouseEvent & mapboxgl.EventData
) => {
  emits('click', data, eventData)
}

const url = computed(() => {
  return '/api/v3/field-view/geojson/jobs'
})

const jobFilterInterceptor: RequestInterceptor = (
  url,
  resourceType,
  currentRequest
) => {
  if (resourceType === 'Source' && url.endsWith('/geojson/jobs')) {
    const body = convertToSnakeCase({
      start: legacyJobStore.selectedStart.value,
      end: legacyJobStore.selectedEnd.value,
      divisions: nullIfEmpty(legacyJobStore.selectedDivisions.value),
      serviceProviders: nullIfEmpty(
        legacyJobStore.selectedServiceProviders.value
      ),
      statuses: nullIfEmpty(legacyJobStore.selectedJobStatuses.value),
      approvalStatuses: nullIfEmpty(
        legacyJobStore.selectedApprovalStatuses.value
      ),
      resourceTypes: nullIfEmpty(legacyJobStore.selectedResourceTypes.value),
      activities: nullIfEmpty(legacyJobStore.selectedActivities.value),
      priorities: nullIfEmpty(legacyJobStore.selectedPriorities.value),
      locations: nullIfEmpty(legacyJobStore.selectedLocations.value),
      batchStatuses: nullIfEmpty(jobsFilterStore.selectedBatchStatuses),
    })

    return {
      ...currentRequest,
      body: JSON.stringify(body),
      method: 'POST',
      headers: {
        ...currentRequest.headers,
        'Content-Type': 'application/json',
      },
    }
  }

  return currentRequest
}

const nullIfEmpty = (value?: any[]) => {
  if (value?.length === 0) {
    return null
  }
  return value
}

useMapboxRequestInterceptor('job-search', jobFilterInterceptor)

const layers = computed<mapboxgl.SymbolLayer[]>(() => {
  return [
    {
      id: props.layerId,
      type: 'symbol',
      source: 'jobs',
      layout: {
        'icon-allow-overlap': true,
        'icon-image': [
          'concat',
          'job-cluster',
          '__',
          '#eeeeee',
          '__',
          ['get', 'point_count'],
        ],
        'text-field': ['get', 'LocationName'],
        'text-font': ['Montserrat Medium', 'Arial Unicode MS Regular'],
        'text-radial-offset': 2,
        'text-line-height': 1.1,
        'text-allow-overlap': false,
        'text-optional': true,
        'text-anchor': 'top',
        'text-letter-spacing': 0.1,
        'text-size': {
          stops: [
            [14, 0],
            [15, 11],
            [15.5, 12],
            [17, 14],
          ],
        },
      },
      paint: {
        'text-halo-color': 'hsl(0, 5%, 0%)',
        'text-color': 'hsl(0, 0%, 100%)',
        'text-halo-width': 2,
        'text-halo-blur': 0.5,
        'text-opacity': {
          stops: [
            [15.5, 0],
            [16, 1],
          ],
        },
      },
    },
    {
      id: 'cluster-count',
      type: 'symbol',
      source: 'jobs',
      layout: {
        'text-field': [
          'case',
          ['has', 'point_count'],
          ['get', 'point_count_abbreviated'],
          ['literal', '1'],
        ],
        'text-optional': false,
        'text-allow-overlap': true,
        'text-font': ['Montserrat Bold', 'Arial Unicode MS Regular'],
        'text-size': 12,
      },
    },
  ]
})

const clusterProperties = {
  LocationName: ['coalesce', ['get', 'LocationName']],
  LocationId: ['coalesce', ['get', 'LocationId']],
}

const filters = computed(() => {
  const filter: any[] = ['all']

  const ids = props.hiddenLocationIds || []
  if (ids.length > 0) {
    filter.push(['!', ['in', ['get', 'LocationId'], ['literal', ids]]])
  }

  return filter
})

return { __sfc: true,props, emits, source, refreshLayer, pollingInterval, jobsPolling, handleVisibilityChange, jobsFilterStore, legacyJobStore, handleClick, url, jobFilterInterceptor, nullIfEmpty, layers, clusterProperties, filters, GeoJsonSource, LayerGroup }
}

})